import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ChevronDown, X } from "lucide-react";

/**
 * Generic multi-select filter dropdown with Select All / Clear All actions.
 *
 * Props:
 * - label:       displayed when nothing is selected (e.g. "All Statuses")
 * - options:     [{ value: string, label: string }]
 * - selected:    string[] of selected values (controlled)
 * - onChange:    (newSelected: string[]) => void
 * - className:   wrapper class for the trigger button (e.g. width)
 * - testId:      base data-testid for the trigger; option items will use `${testId}-option-${value}`
 */
export default function MultiSelectFilter({
  label,
  options,
  selected,
  onChange,
  className = "",
  testId,
}) {
  const allSelected = selected.length === options.length && options.length > 0;
  const someSelected = selected.length > 0 && !allSelected;

  const toggle = (value) => {
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  const selectAll = () => onChange(options.map((o) => o.value));
  const clearAll = () => onChange([]);

  const triggerLabel =
    selected.length === 0
      ? label
      : selected.length === 1
        ? (options.find((o) => o.value === selected[0])?.label || selected[0])
        : `${selected.length} Selected`;

  const hasActive = selected.length > 0;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={`justify-between ${className} ${hasActive ? "border-accent text-accent" : ""}`}
          data-testid={testId}
        >
          <span className={`truncate ${hasActive ? "font-medium" : ""}`}>{triggerLabel}</span>
          <ChevronDown className="h-4 w-4 ml-2 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-60 p-0" align="start">
        <div className="flex items-center justify-between px-2 py-1.5 border-b border-slate-100 gap-1">
          <button
            type="button"
            onClick={selectAll}
            className="text-[11px] font-medium px-2 py-1 rounded hover:bg-accent/10 text-accent disabled:opacity-40 disabled:hover:bg-transparent"
            disabled={allSelected}
            data-testid={testId ? `${testId}-select-all` : undefined}
          >
            Select All
          </button>
          <button
            type="button"
            onClick={clearAll}
            className="text-[11px] font-medium px-2 py-1 rounded hover:bg-red-50 text-slate-500 hover:text-red-600 disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-slate-500 inline-flex items-center"
            disabled={selected.length === 0}
            data-testid={testId ? `${testId}-clear-all` : undefined}
          >
            <X className="h-3 w-3 mr-0.5" />
            Clear
          </button>
        </div>
        <div className="max-h-64 overflow-auto py-1">
          {options.map((opt) => {
            const checked = selected.includes(opt.value);
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => toggle(opt.value)}
                className={`w-full flex items-center gap-2 px-2.5 py-1.5 text-left text-[13px] hover:bg-slate-50 ${checked ? "bg-accent/5" : ""}`}
                data-testid={testId ? `${testId}-option-${opt.value}` : undefined}
              >
                <Checkbox checked={checked} className="pointer-events-none" />
                <span className={checked ? "font-medium text-slate-800" : "text-slate-700"}>{opt.label}</span>
              </button>
            );
          })}
        </div>
        {someSelected && (
          <div className="px-2 py-1 border-t border-slate-100 text-[10px] text-muted-foreground">
            {selected.length} of {options.length} selected
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
